import { Component, OnInit, AfterViewInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { BreadcrumbComponent } from '@app/lib/components/breadcrumb/breadcrumb.component';
import { IResponse } from '@app/lib/models/response.model';
import { ButtonComponent } from '@lib/components/button/button.component';
import { IBreadcrumb } from '@lib/models/breadcrumb.model';
import { IMyAssignment, IMyAssignmentParams } from '../models/assigned-task.model';
import { ITable } from '../models/common.model';
import { AssignedTaskService } from '../services/assigned-task.service';
import { CaseDocumentsComponent } from '../case-documents/case-documents.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DatePipe, NgFor } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ECourtId } from '@app/dashboard/models/constant';
import { ILoginData } from '@app/lib/models/login.model';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'; // <-- ADDED
import { finalize } from 'rxjs'; // <-- ADDED
import { CaseNumberSearchComponent } from '../widget/case-number-search/case-number-search.component';
import { MatOption } from "@angular/material/core";

@Component({
  selector: 'app-my-assignments',
  standalone: true,
  imports: [
    NgFor,
    DatePipe,
    ButtonComponent,
    BreadcrumbComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ScrollingModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    ReactiveFormsModule,
    CaseDocumentsComponent,
    MatRadioModule,
    MatProgressSpinnerModule, // <-- ADDED
    CaseNumberSearchComponent,

  ],
  templateUrl: './my-assignments.component.html',
  styleUrl: './my-assignments.component.scss'
})
export class MyAssignmentsComponent implements OnInit, AfterViewInit {

  @ViewChild(MatSort) matSortRef!: MatSort;

  public isLoading: WritableSignal<boolean> = signal(false); // <-- ADDED LOADING SIGNAL
  public caseNumber: WritableSignal<string> = signal('');

  userType: string = "";

  public caseNumberCtrl: FormControl<string> = new FormControl('', { nonNullable: true });
  searchTypeCtrl = new FormControl('hearing', { nonNullable: true });

  public breadcrumbs: WritableSignal<IBreadcrumb[]> = signal([
    { label: 'Case' },
    { label: 'Pending Cases' },
  ]);
  isSupremeCourtFlg: boolean = false;
  isCOAFlg: boolean = false;


  public hearingScheduleTbl: WritableSignal<ITable> = signal({
    columns: [
      {
        dataIndex: 'appealNumber',
        title: this.isSupremeCourtFlg ? 'Appeal Number' : 'Case Number',
      },
      {
        dataIndex: 'appellant',
        title: 'Plaintiff(s)/Claimant(s)',
      },
      {
        dataIndex: 'respondent',
        title: 'Defendent(s)',
      },
      {
        dataIndex: 'hearingDate',
        title: 'Hearing Date',
      }
    ]
  });
  public displayedColumns: WritableSignal<string[]> = signal([]);

  public dataSource: MatTableDataSource<IMyAssignment> = new MatTableDataSource();

  constructor(public service: AssignedTaskService) {
    this.displayedColumns.set(this.hearingScheduleTbl().columns.map(col => col.dataIndex));
  }

  ngOnInit() {
    this.getCourtNameData();
    this.searchTypeCtrl.valueChanges.subscribe((selectedType: string) => {
      this.onRadioChange(selectedType);
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.matSortRef;
    this.onCaseSearchHearing();
  }

  updateTableHeaders() {
    this.hearingScheduleTbl.update(currentTable => {
      const appealCol = currentTable.columns.find(c => c.dataIndex === 'appealNumber');
      if (appealCol) {
        appealCol.title = this.isSupremeCourtFlg ? 'Appeal Number' : 'Case Number';
      }
      return { ...currentTable };
    });
  }

  onCaseSearchHearing() {
    const queryParams: IMyAssignmentParams = {
      appealNumber: this.caseNumberCtrl.value || '',
      limit: 1500,
      page: 1,
      start: 0
    };
    this.caseNumber.set('');
    this.caseNumberCtrl.reset();

    this.isLoading.set(true); // <-- SET LOADING TO TRUE
    this.service.getMyAssignmentsHearing(queryParams)
      .pipe(
        finalize(() => this.isLoading.set(false)) // <-- AUTOMATICALLY STOPS LOADING ON SUCCESS OR ERROR
      )
      .subscribe({
        next: (res: IResponse<IMyAssignment[]>) => {
          this.dataSource.data = (res.data || []);
        }
      });
  }

  public onCaseSearchOptionSelected(item: any): void {
    const caseNo = item?.caseNumber || item?.courtCaseNumber || '';
    if (caseNo) {
      this.caseNumberCtrl.patchValue(caseNo);
    }
  }

  onCaseSearchJudgement() {
    const queryParams: IMyAssignmentParams = {
      caseNumber: this.caseNumberCtrl.value || '',
      limit: 1500,
      page: 1,
      start: 0
    };
    this.caseNumber.set('');
    this.caseNumberCtrl.reset();

    this.isLoading.set(true); // <-- SET LOADING TO TRUE
    this.service.getMyAssignmentsJudgement(queryParams)
      .pipe(
        finalize(() => this.isLoading.set(false)) // <-- AUTOMATICALLY STOPS LOADING ON SUCCESS OR ERROR
      )
      .subscribe({
        next: (res: IResponse<IMyAssignment[]>) => {
          const formattedData = (res.data || []).map(item => ({
            ...item,
            hearingDate: item.judgementDate || item.hearingDate
          }));
          this.dataSource.data = formattedData;
        }
      });
  }

  onCaseSearch() {
    if (this.searchTypeCtrl.value === 'hearing') {
      this.onCaseSearchHearing();
    } else {
      this.onCaseSearchJudgement();
    }
  }

  public appealNumberClickHandler(row: IMyAssignment) {
    this.caseNumber.set(row.appealNumber);
  }

  onRadioChange(type: string) {
    this.hearingScheduleTbl.update(currentTable => {
      const appealCol = currentTable.columns.find(c => c.dataIndex === 'hearingDate');
      if (appealCol) {
        appealCol.title = type === 'hearing' ? 'Hearing Date' : 'Judgement Date';
      }
      return { ...currentTable };
    });


    // 3. Perform backend search
    if (type === 'hearing') {
      this.onCaseSearchHearing();
    } else {
      this.onCaseSearchJudgement();
    }
  }

  getCourtNameData(): ILoginData | null {
    try {
      const userData = localStorage.getItem('userData') as string;
      const userData2 = userData ? JSON.parse(userData) : null;
      const courtIdData = userData2?.courtId;

      if (courtIdData && courtIdData === ECourtId.SC_ID) {
        this.isSupremeCourtFlg = true;
        this.userType = 'admin'
        this.breadcrumbs.set([{ label: 'Appeal' }, { label: 'Pending Appeals' }]);
      } else if (courtIdData && courtIdData === ECourtId.COA_ID) {
        this.isCOAFlg = true;
        this.userType = 'lawyer'
      }
      return userData2;
    } catch (err) {
      return null;
    } finally {
      this.updateTableHeaders();
    }
  }
}